import * as React from 'react';
import {
    Title,
    useTranslate,
    useGetList,
    usePermissions,
} from 'react-admin';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';

// Icons
import RouteIcon from '@mui/icons-material/Route';
import PlaceIcon from '@mui/icons-material/Place';
import PeopleIcon from '@mui/icons-material/People';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LandscapeIcon from '@mui/icons-material/Landscape';

import { useNavigate } from 'react-router-dom';
import { customerConfig } from '../../customerConfig';

// ─── Stat Card ───────────────────────────────────────────────────────────────

interface StatCardProps {
    title: string;
    value: number | undefined;
    icon: React.ReactElement;
    color: string;
    loading?: boolean;
    linkTo?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, loading, linkTo }) => {
    const navigate = useNavigate();
    return (
        <Card
            sx={{
                cursor: linkTo ? 'pointer' : 'default',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': linkTo ? {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                } : {},
            }}
            onClick={() => linkTo && navigate(linkTo)}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {title}
                        </Typography>
                        {loading ? (
                            <Skeleton variant="text" width={60} height={48} />
                        ) : (
                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                {value ?? '–'}
                            </Typography>
                        )}
                    </Box>
                    <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
                        {icon}
                    </Avatar>
                </Box>
            </CardContent>
        </Card>
    );
};

// ─── Recent Items Card ───────────────────────────────────────────────────────

interface RecentItem {
    id: number;
    name: string | Record<string, string>;
    updatedAt?: string;
    createdAt?: string;
}

interface RecentCardProps {
    title: string;
    icon: React.ReactElement;
    items: RecentItem[];
    loading: boolean;
    resource: string;
    color: string;
}

const RecentCard: React.FC<RecentCardProps> = ({ title, icon, items, loading, resource, color }) => {
    const navigate = useNavigate();

    const getName = (item: RecentItem): string => {
        if (typeof item.name === 'string') return item.name;
        if (item.name && typeof item.name === 'object') {
            return item.name.sv || item.name.en || item.name.de || Object.values(item.name)[0] || `#${item.id}`;
        }
        return `#${item.id}`;
    };

    const getDate = (item: RecentItem): string => {
        const dateStr = item.updatedAt || item.createdAt;
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('sv-SE', {
                year: 'numeric', month: 'short', day: 'numeric',
            });
        } catch {
            return '';
        }
    };

    return (
        <Card>
            <CardContent sx={{ pb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar sx={{ bgcolor: color, width: 32, height: 32 }}>
                        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                </Box>
            </CardContent>
            {loading ? (
                <Box sx={{ px: 2, pb: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} variant="text" height={40} />
                    ))}
                </Box>
            ) : (
                <List dense sx={{ pt: 0 }}>
                    {items.map((item, index) => (
                        <React.Fragment key={item.id}>
                            {index > 0 && <Divider />}
                            <ListItem
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    py: 0.5,
                                }}
                                onClick={() => navigate(`/${resource}/${item.id}`)}
                                secondaryAction={
                                    <EditIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                }
                            >
                                <ListItemText
                                    primary={getName(item)}
                                    secondary={getDate(item)}
                                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                />
                            </ListItem>
                        </React.Fragment>
                    ))}
                    {items.length === 0 && (
                        <ListItem>
                            <ListItemText
                                primary="–"
                                primaryTypographyProps={{ color: 'text.disabled', textAlign: 'center' }}
                            />
                        </ListItem>
                    )}
                </List>
            )}
        </Card>
    );
};

// ─── Quick Action Button ─────────────────────────────────────────────────────

interface QuickActionProps {
    label: string;
    icon: React.ReactElement;
    to: string;
}

const QuickAction: React.FC<QuickActionProps> = ({ label, icon, to }) => {
    const navigate = useNavigate();
    return (
        <Button
            variant="outlined"
            startIcon={icon}
            onClick={() => navigate(to)}
            sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                py: 1.2,
                color: 'text.primary',
                borderColor: '#e0e0e3',
                '&:hover': {
                    borderColor: customerConfig.theme.primaryColor,
                    bgcolor: 'transparent',
                },
            }}
            fullWidth
        >
            {label}
        </Button>
    );
};

// ─── Main Dashboard ──────────────────────────────────────────────────────────

export const Dashboard = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const isAdmin = permissions === 'admin';

    // Fetch counts (perPage=1 to only get total)
    const { total: pathCount, isLoading: pathsLoading } = useGetList('paths', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
    });
    const { total: poiCount, isLoading: poisLoading } = useGetList('pois', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
    });
    const { total: aoiCount, isLoading: aoisLoading } = useGetList('aois', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
    });
    const { total: infoCount, isLoading: infosLoading } = useGetList('information', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'DESC' },
    });
    const { total: userCount, isLoading: usersLoading } = useGetList(
        'users',
        { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'DESC' } },
        { enabled: isAdmin },
    );

    // Fetch recent items
    const { data: recentPaths = [], isLoading: recentPathsLoading } = useGetList('paths', {
        pagination: { page: 1, perPage: 5 },
        sort: { field: 'updatedAt', order: 'DESC' },
    });
    const { data: recentPois = [], isLoading: recentPoisLoading } = useGetList('pois', {
        pagination: { page: 1, perPage: 5 },
        sort: { field: 'updatedAt', order: 'DESC' },
    });
    const { data: recentInfos = [], isLoading: recentInfosLoading } = useGetList('information', {
        pagination: { page: 1, perPage: 5 },
        sort: { field: 'updatedAt', order: 'DESC' },
    });

    const primaryColor = customerConfig.theme.primaryColor;
    const secondaryColor = customerConfig.theme.secondaryColor;

    return (
        <Box sx={{ mt: 1 }}>
            <Title title={translate('ra.page.dashboard')} />

            {/* Welcome */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                {translate('resources.page.dashboard.welcome', { name: customerConfig.displayName })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('resources.page.dashboard.subtitle')}
            </Typography>

            {/* Stat Cards */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
                    <StatCard
                        title={translate('resources.menu.paths')}
                        value={pathCount}
                        icon={<RouteIcon />}
                        color={primaryColor}
                        loading={pathsLoading}
                        linkTo="/paths"
                    />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
                    <StatCard
                        title={translate('resources.menu.pointOfInterests')}
                        value={poiCount}
                        icon={<PlaceIcon />}
                        color={secondaryColor}
                        loading={poisLoading}
                        linkTo="/pois"
                    />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
                    <StatCard
                        title={translate('resources.menu.areaOfInterests')}
                        value={aoiCount}
                        icon={<LandscapeIcon />}
                        color="#FF7043"
                        loading={aoisLoading}
                        linkTo="/aois"
                    />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
                    <StatCard
                        title={translate('resources.menu.information')}
                        value={infoCount}
                        icon={<InfoIcon />}
                        color="#AB47BC"
                        loading={infosLoading}
                        linkTo="/information"
                    />
                </Box>
                {isAdmin && (
                    <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
                        <StatCard
                            title={translate('resources.menu.users')}
                            value={userCount}
                            icon={<PeopleIcon />}
                            color="#78909C"
                            loading={usersLoading}
                            linkTo="/users"
                        />
                    </Box>
                )}
            </Box>

            {/* Recent Items + Quick Actions */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Recent Paths */}
                <Box sx={{ flex: '1 1 300px', minWidth: 280 }}>
                    <RecentCard
                        title={translate('resources.page.dashboard.recentPaths')}
                        icon={<RouteIcon />}
                        items={recentPaths as any[]}
                        loading={recentPathsLoading}
                        resource="paths"
                        color={primaryColor}
                    />
                </Box>

                {/* Recent POIs */}
                <Box sx={{ flex: '1 1 300px', minWidth: 280 }}>
                    <RecentCard
                        title={translate('resources.page.dashboard.recentPois')}
                        icon={<PlaceIcon />}
                        items={recentPois as any[]}
                        loading={recentPoisLoading}
                        resource="pois"
                        color={secondaryColor}
                    />
                </Box>

                {/* Quick Actions + Recent Information */}
                <Box sx={{ flex: '1 1 300px', minWidth: 280 }}>
                    {isAdmin && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                                    {translate('resources.page.dashboard.quickActions')}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <QuickAction
                                        label={translate('resources.page.dashboard.newPath')}
                                        icon={<AddIcon />}
                                        to="/paths/create"
                                    />
                                    <QuickAction
                                        label={translate('resources.page.dashboard.newPoi')}
                                        icon={<AddIcon />}
                                        to="/pois/create"
                                    />
                                    <QuickAction
                                        label={translate('resources.page.dashboard.newInfo')}
                                        icon={<AddIcon />}
                                        to="/information/create"
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                    <RecentCard
                        title={translate('resources.page.dashboard.recentInfo')}
                        icon={<InfoIcon />}
                        items={recentInfos as any[]}
                        loading={recentInfosLoading}
                        resource="information"
                        color="#AB47BC"
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default Dashboard;